import { createFileRoute } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { DateInput } from '@/components/date-input';
import { Checkbox, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { apiDownload } from '@/lib/api';
import { todayIso } from '@/lib/dates';
import {
  EXPORT_LISTS,
  EXPORT_PERIODS,
  EXPORT_REPORTS,
  exportSummary,
  journalExportPath,
  listExportPath,
  reportDates,
  reportExportPath,
  type ExportFormat,
  type ExportListSpec,
} from '@/lib/export';
import { useOrgId } from '@/lib/org';
import { useCompanySettings } from '@/lib/settings';

export const Route = createFileRoute('/settings/export')({ component: ExportData });

const FORMATS: Array<[ExportFormat, string]> = [
  ['xlsx', 'Excel (.xlsx)'],
  ['csv', 'CSV'],
];

/** One download after another; the browser would otherwise drop all but the first. */
async function downloadAll(
  jobs: Array<{ name: string; path: string; fallback: string }>,
): Promise<void> {
  let done = 0;
  const failed: string[] = [];
  for (const job of jobs) {
    try {
      await apiDownload(job.path, job.fallback);
      done += 1;
    } catch {
      failed.push(job.name);
    }
  }
  toast(exportSummary(done, failed), { tone: failed.length ? 'error' : 'success' });
}

/** Settings › Export data (document 06 G5): reports and lists to Excel or CSV, the full journal. */
function ExportData() {
  const orgId = useOrgId();
  return (
    <>
      <PageHeader
        title="Export data"
        description="Download reports, lists and the journal as Excel or CSV files. Lists use the same columns as Import data, so they import into another client."
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          <ReportsSection orgId={orgId} />
          <ListsSection orgId={orgId} />
          <JournalSection orgId={orgId} />
        </div>
      )}
    </>
  );
}

function Section({
  title,
  description,
  children,
  testId,
}: {
  title: string;
  description: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <section className="flex flex-col gap-4 rounded-lg border bg-card p-5" data-testid={testId}>
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

function FormatSelect({
  value,
  onChange,
  label,
}: {
  value: ExportFormat;
  onChange: (f: ExportFormat) => void;
  label: string;
}) {
  return (
    <Field label={label} className="w-[180px]">
      {(id) => (
        <NativeSelect
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value as ExportFormat)}
        >
          {FORMATS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </NativeSelect>
      )}
    </Field>
  );
}

function ReportsSection({ orgId }: { orgId: string }) {
  const company = useCompanySettings();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [period, setPeriod] = useState('this_year_to_date');
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [busy, setBusy] = useState(false);
  const all = picked.size === EXPORT_REPORTS.length;
  const toggle = (key: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  async function run() {
    const fy = company.data?.advanced.fiscalYearStart ?? 1;
    const today = todayIso();
    setBusy(true);
    try {
      await downloadAll(
        EXPORT_REPORTS.filter((r) => picked.has(r.key)).map((r) => ({
          name: r.name,
          path: reportExportPath(orgId, r, reportDates(r, period, today, fy), format),
          fallback: `${r.key}.${format}`,
        })),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      title="Reports"
      description="Each report downloads as its own file for the period you choose."
      testId="export-reports"
    >
      <Checkbox
        label={all ? 'Clear all' : 'Select all'}
        checked={all}
        onChange={() => setPicked(all ? new Set() : new Set(EXPORT_REPORTS.map((r) => r.key)))}
      />
      <div className="grid gap-2 sm:grid-cols-2">
        {EXPORT_REPORTS.map((r) => (
          <Checkbox
            key={r.key}
            label={r.name}
            checked={picked.has(r.key)}
            onChange={() => toggle(r.key)}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Report period" className="w-[220px]">
          {(id) => (
            <NativeSelect id={id} value={period} onChange={(e) => setPeriod(e.target.value)}>
              {EXPORT_PERIODS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <FormatSelect label="Format" value={format} onChange={setFormat} />
        <Button
          type="button"
          onClick={() => void run()}
          disabled={busy || picked.size === 0}
          className="gap-2"
          data-testid="export-reports-button"
        >
          <Download className="size-4" />
          {busy ? 'Exporting…' : `Export ${picked.size ? `${picked.size} ` : ''}reports`}
        </Button>
      </div>
    </Section>
  );
}

function ListsSection({ orgId }: { orgId: string }) {
  const [picked, setPicked] = useState<Set<ExportListSpec['key']>>(new Set());
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [busy, setBusy] = useState(false);
  const toggle = (key: ExportListSpec['key']) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  async function run() {
    setBusy(true);
    try {
      await downloadAll(
        EXPORT_LISTS.filter((l) => picked.has(l.key)).map((l) => ({
          name: l.name,
          path: listExportPath(orgId, l.key, format),
          fallback: `${l.key}.${format}`,
        })),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      title="Lists"
      description="Chart of accounts, customers, vendors and products and services, in the Import data columns."
      testId="export-lists"
    >
      <div className="grid gap-2 sm:grid-cols-2">
        {EXPORT_LISTS.map((l) => (
          <Checkbox
            key={l.key}
            label={l.name}
            checked={picked.has(l.key)}
            onChange={() => toggle(l.key)}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <FormatSelect label="Format" value={format} onChange={setFormat} />
        <Button
          type="button"
          onClick={() => void run()}
          disabled={busy || picked.size === 0}
          className="gap-2"
          data-testid="export-lists-button"
        >
          <Download className="size-4" />
          {busy ? 'Exporting…' : `Export ${picked.size ? `${picked.size} ` : ''}lists`}
        </Button>
      </div>
    </Section>
  );
}

function JournalSection({ orgId }: { orgId: string }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [busy, setBusy] = useState(false);
  const bad = Boolean(from && to && from > to);

  async function run() {
    setBusy(true);
    try {
      await downloadAll([
        {
          name: 'Journal',
          path: journalExportPath(orgId, { from, to }, format),
          fallback: `journal.${format}`,
        },
      ]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      title="Journal"
      description="Every journal line of this client, posted and reversed, with the account, name, VAT code, currency and home-currency amounts. Leave the dates empty for the whole journal."
      testId="export-journal"
    >
      <div className="flex flex-wrap items-end gap-3">
        <Field label="From" className="w-[160px]">
          {(id) => <DateInput id={id} value={from} onChange={setFrom} />}
        </Field>
        <Field
          label="To"
          className="w-[160px]"
          error={bad ? 'The end date is before the start date' : undefined}
        >
          {(id) => <DateInput id={id} value={to} onChange={setTo} invalid={bad} />}
        </Field>
        <FormatSelect label="Format" value={format} onChange={setFormat} />
        <Button
          type="button"
          onClick={() => void run()}
          disabled={busy || bad}
          className="gap-2"
          data-testid="export-journal-button"
        >
          <Download className="size-4" />
          {busy ? 'Exporting…' : 'Export journal'}
        </Button>
      </div>
    </Section>
  );
}
